/**
 * Renders one custom dataset as a chart (time_series line, bar, or table).
 * Same format as other sections: Key Metrics (1-4), optional Overview, Relevance, Equity, Methodology.
 * Uses .chart-section and .chart-container so PDF/Word report can capture canvas as image.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import type { CustomDataset, CustomDatasetRow } from '../../services/customDatasetsService';
import { narrativeMethodologyTabLabel } from '../../constants/narrativeTabLabels';
import '../charts/ChartLayout.css';

Chart.register(...registerables);

/** Sanitize for minimal safe HTML (same allowed tags as CustomSectionBlock). */
function sanitizeHtml(html: string): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  const allowedTags = ['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'A', 'UL', 'OL', 'LI', 'H4', 'H5'];
  const walk = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (!allowedTags.includes(el.tagName)) {
        const span = document.createElement('span');
        while (el.firstChild) span.appendChild(el.firstChild);
        if (el.parentNode) {
          el.parentNode.replaceChild(span, el);
        } else {
          el.appendChild(span);
        }
        let c = span.firstChild;
        while (c) {
          const next = c.nextSibling;
          walk(c);
          c = next;
        }
        return;
      }
      if (el.tagName === 'A') {
        const href = el.getAttribute('href') || '';
        if (!href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('/')) {
          el.removeAttribute('href');
        }
      }
    }
    let child = node.firstChild;
    while (child) {
      const next = child.nextSibling;
      walk(child);
      child = next;
    }
  };
  walk(div);
  return div.innerHTML;
}

type NarrativeView = 'overview' | 'relevance' | 'equity' | 'methodology';

const SERIES_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];

interface CustomDataChartProps {
  dataset: CustomDataset;
}

function buildSeriesGroups(rows: CustomDatasetRow[]): { labels: string[]; series: { label: string; values: (number | null)[] }[] } {
  const bySeries = new Map<string, { label: string; value: number }[]>();
  const labelOrder: string[] = [];
  const labelSet = new Set<string>();

  rows.forEach((r) => {
    const seriesKey = r.series_label ?? '__single__';
    if (!bySeries.has(seriesKey)) {
      bySeries.set(seriesKey, []);
    }
    bySeries.get(seriesKey)!.push({ label: r.label, value: r.value });
    if (!labelSet.has(r.label)) {
      labelSet.add(r.label);
      labelOrder.push(r.label);
    }
  });

  const labels = [...labelOrder];
  const series: { label: string; values: (number | null)[] }[] = [];

  bySeries.forEach((points, key) => {
    const valueByLabel = new Map(points.map((p) => [p.label, p.value]));
    const seriesLabel = key === '__single__' ? 'Value' : key;
    series.push({
      label: seriesLabel,
      values: labels.map((l) => valueByLabel.has(l) ? valueByLabel.get(l)! : null)
    });
  });

  return { labels, series };
}

export const CustomDataChart: React.FC<CustomDataChartProps> = ({ dataset }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const rows = useMemo(() => dataset.rows ?? [], [dataset.rows]);
  const metrics = dataset.metrics ?? [];
  const overview = dataset.overview_html?.trim();
  const relevance = dataset.relevance_html?.trim();
  const equity = dataset.equity_html?.trim();
  const methodology = dataset.methodology_html?.trim();
  const hasNarrative = !!(overview || relevance || equity || methodology);
  const firstView: NarrativeView = overview ? 'overview' : relevance ? 'relevance' : equity ? 'equity' : 'methodology';
  const [activeView, setActiveView] = useState<NarrativeView>(firstView);
  const overviewTitle = dataset.overview_title?.trim() || 'Overview';
  const relevanceTitle = dataset.relevance_title?.trim() || 'Relevance';
  const equityTitle = dataset.equity_title?.trim() || 'Equity';
  const methodologyTitle = narrativeMethodologyTabLabel(dataset.methodology_title);
  useEffect(() => {
    setActiveView(firstView);
  }, [dataset.id, firstView]);

  useEffect(() => {
    if (dataset.chart_type === 'table' || !canvasRef.current || rows.length === 0) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
    const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
    if (existing) existing.destroy();

    const { labels, series } = buildSeriesGroups(rows);

    if (dataset.chart_type === 'time_series') {
      chartRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: series.map((s, i) => ({
            label: s.label,
            data: s.values,
            borderColor: SERIES_COLORS[i % SERIES_COLORS.length],
            backgroundColor: 'transparent',
            tension: 0.2,
            fill: false,
            pointRadius: 4,
            borderWidth: 2
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: series.length > 1 },
            tooltip: { mode: 'index', intersect: false }
          },
          scales: {
            x: {
              title: { display: true, text: dataset.x_axis_label ?? 'Label' }
            },
            y: {
              beginAtZero: true,
              title: { display: true, text: dataset.y_axis_label ?? 'Value' }
            }
          },
          interaction: { mode: 'index', intersect: false }
        }
      });
    } else {
      // bar
      chartRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: series.map((s, i) => ({
            label: s.label,
            data: s.values,
            backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length],
            borderColor: SERIES_COLORS[i % SERIES_COLORS.length],
            borderWidth: 1
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: series.length > 1 },
            tooltip: { mode: 'index', intersect: false }
          },
          scales: {
            x: {
              title: { display: true, text: dataset.x_axis_label ?? 'Label' }
            },
            y: {
              beginAtZero: true,
              title: { display: true, text: dataset.y_axis_label ?? 'Value' }
            }
          },
          interaction: { mode: 'index', intersect: false }
        }
      });
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [dataset.id, dataset.chart_type, dataset.x_axis_label, dataset.y_axis_label, rows]);

  const descriptionHtml = dataset.description ? sanitizeHtml(dataset.description) : '';

  const titleBlock = (
    <div className="chart-title-section">
      <h3>{dataset.title}</h3>
      {descriptionHtml && (
        <p className="chart-description-text" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
      )}
      {metrics.length > 0 && (
        <div className="key-metrics-after-title">
          <KeyMetrics>
            {metrics.map((m) => (
              <KeyMetric key={m.id} value={m.value} label={m.label} color={m.color_hex || undefined} />
            ))}
          </KeyMetrics>
        </div>
      )}
    </div>
  );

  let leftContent: React.ReactNode;
  if (rows.length === 0) {
    leftContent = (
      <div className="chart-container">
        <p style={{ padding: '1rem', color: '#666' }}>No data for this chart.</p>
      </div>
    );
  } else if (dataset.chart_type === 'table') {
    const { labels, series } = buildSeriesGroups(rows);
    const hasSeries = series.length > 1;
    leftContent = (
      <div className="chart-container">
        <div className="custom-data-table-wrapper" style={{ overflowX: 'auto' }}>
          <table className="custom-data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{dataset.x_axis_label ?? 'Label'}</th>
                {hasSeries ? series.map((s) => (
                  <th key={s.label} style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #ddd' }}>{s.label}</th>
                )) : (
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #ddd' }}>{dataset.y_axis_label ?? 'Value'}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {labels.map((label, i) => (
                <tr key={`${label}-${i}`}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{label}</td>
                  {hasSeries
                    ? series.map((s) => (
                        <td key={s.label} style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #eee' }}>
                          {s.values[i] != null ? Number(s.values[i]).toLocaleString() : '—'}
                        </td>
                      ))
                    : (
                        <td style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #eee' }}>
                          {series[0]?.values[i] != null ? Number(series[0].values[i]).toLocaleString() : '—'}
                        </td>
                      )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  } else {
    leftContent = (
      <div className="chart-container">
        <canvas ref={canvasRef} style={{ height: '320px' }} />
      </div>
    );
  }

  const narrativeBlock = hasNarrative ? (
    <div className="chart-right-side">
      <div className="toggle-buttons">
        {overview && (
          <button
            type="button"
            className={`toggle-btn ${activeView === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveView('overview')}
          >
            {overviewTitle}
          </button>
        )}
        {relevance && (
          <button
            type="button"
            className={`toggle-btn ${activeView === 'relevance' ? 'active' : ''}`}
            onClick={() => setActiveView('relevance')}
          >
            {relevanceTitle}
          </button>
        )}
        {equity && (
          <button
            type="button"
            className={`toggle-btn ${activeView === 'equity' ? 'active' : ''}`}
            onClick={() => setActiveView('equity')}
          >
            {equityTitle}
          </button>
        )}
        {methodology && (
          <button
            type="button"
            className={`toggle-btn ${activeView === 'methodology' ? 'active' : ''}`}
            onClick={() => setActiveView('methodology')}
          >
            {methodologyTitle}
          </button>
        )}
      </div>
      <div className="content-sections">
        {overview && (
          <div className={`content-section ${activeView === 'overview' ? '' : 'hidden'}`}>
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(overview) }} />
          </div>
        )}
        {relevance && (
          <div className={`content-section ${activeView === 'relevance' ? '' : 'hidden'}`}>
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(relevance) }} />
          </div>
        )}
        {equity && (
          <div className={`content-section ${activeView === 'equity' ? '' : 'hidden'}`}>
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(equity) }} />
          </div>
        )}
        {methodology && (
          <div className={`content-section ${activeView === 'methodology' ? '' : 'hidden'}`}>
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(methodology) }} />
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="chart-section custom-data-chart" id={`custom-dataset-${dataset.id}`}>
      <div className="chart-content">
        {titleBlock}
        <div className={`chart-content-layout ${!hasNarrative ? 'single-column' : ''}`}>
          <div className="chart-left-side">
            {leftContent}
          </div>
          {narrativeBlock}
        </div>
      </div>
    </div>
  );
};

export default CustomDataChart;
